import { useCallback, useEffect, useState } from "react";
import { apiFetch, ApiRequestError } from "../lib/api.js";
import { useToast } from "../components/Toast.js";
import type {
  TenantCustomerRoleType,
  TenantDefaultUserMappingDetail
} from "../lib/customers.js";

interface TenantPersonRow {
  account_id: string;
  email: string;
  display_name: string | null;
  status?: string;
}

interface Props {
  onClose: () => void;
}

/**
 * ADR-0075 — Default coverage modal.
 *
 * Edits the tenant-level fallback mappings used when no customer matches.
 * Same shape as the per-customer mapping table, but scoped to the tenant
 * (no customer_id) and posted to /api/tenant/admin/customers/default-coverage/*.
 */
export function DefaultCoverageModal({ onClose }: Props) {
  const toast = useToast();
  const [mappings, setMappings] = useState<TenantDefaultUserMappingDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [people, setPeople] = useState<TenantPersonRow[]>([]);
  const [roleTypes, setRoleTypes] = useState<TenantCustomerRoleType[]>([]);
  const [addOpen, setAddOpen] = useState(false);
  const [addUserId, setAddUserId] = useState("");
  const [addRoleId, setAddRoleId] = useState("");
  const [addError, setAddError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Silent reload — does not toggle `loading`, so post-mutation refreshes
  // don't unmount the table and flash the body.
  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await apiFetch<{ mappings: TenantDefaultUserMappingDetail[] }>(
        "/api/tenant/admin/customers/default-coverage"
      );
      setMappings(res.mappings);
    } catch (e) {
      setError(e instanceof ApiRequestError ? e.message : e instanceof Error ? e.message : String(e));
    }
  }, []);

  useEffect(() => {
    void (async () => {
      setLoading(true);
      try {
        await load();
      } finally {
        setLoading(false);
      }
    })();
  }, [load]);

  useEffect(() => {
    if (!addOpen) return;
    void (async () => {
      try {
        const [pe, rt] = await Promise.all([
          apiFetch<{ members: TenantPersonRow[] }>("/api/tenant/admin/users"),
          apiFetch<{ role_types: TenantCustomerRoleType[] }>("/api/tenant/admin/customer-role-types")
        ]);
        setPeople((pe.members ?? []).filter((m) => m.status === "active" || m.status === undefined));
        setRoleTypes(rt.role_types ?? []);
      } catch {
        // Non-fatal — picker just shows empty.
      }
    })();
  }, [addOpen]);

  async function addMapping() {
    if (!addUserId || !addRoleId) return;
    setBusy(true);
    setAddError(null);
    try {
      await apiFetch("/api/tenant/admin/customers/default-coverage/users", {
        method: "POST",
        body: { user_id: addUserId, role_type_id: addRoleId }
      });
      setAddOpen(false);
      setAddUserId("");
      setAddRoleId("");
      await load();
    } catch (e) {
      setAddError(e instanceof ApiRequestError ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function setPrimary(mappingId: string) {
    try {
      await apiFetch(`/api/tenant/admin/customers/default-coverage/users/${mappingId}`, {
        method: "PATCH",
        body: { is_primary: true }
      });
      await load();
    } catch (e) {
      const msg = e instanceof ApiRequestError ? e.message : e instanceof Error ? e.message : String(e);
      toast.push({ kind: "error", message: msg });
    }
  }

  async function removeMapping(m: TenantDefaultUserMappingDetail) {
    try {
      await apiFetch(`/api/tenant/admin/customers/default-coverage/users/${m.id}`, {
        method: "DELETE"
      });
      await load();
    } catch (e) {
      const msg = e instanceof ApiRequestError ? e.message : e instanceof Error ? e.message : String(e);
      toast.push({ kind: "error", message: msg });
    }
  }

  const mappedAccountIds = new Set(mappings.map((m) => m.user_id));
  const availablePeople = people.filter((p) => !mappedAccountIds.has(p.account_id));

  return (
    <div className="modalOverlay" role="presentation" onClick={onClose}>
      <div
        className="modal modalScrollable"
        role="dialog"
        aria-modal="true"
        aria-label="Default coverage"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 720, width: "90vw" }}
      >
        <div className="modalScrollHeader">
          <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 8 }}>Default coverage</div>
          <div className="help muted">
            Used when a watch row's account code doesn't match any customer, or the customer
            hierarchy exhausts without a mapped user.
          </div>
        </div>

        <div className="modalScrollBody" style={{ paddingTop: 12, paddingBottom: 16 }}>
          {error && <div className="help" style={{ color: "var(--danger-text)" }}>{error}</div>}
          {loading && <div className="muted">Loading…</div>}

          {!loading && (
            <>
              <div style={{ display: "flex", alignItems: "center", marginBottom: 10 }}>
                <div style={{ fontWeight: 600 }}>Mapped users</div>
                <div style={{ marginLeft: "auto" }}>
                  <button className="btn btnPrimary" onClick={() => setAddOpen((v) => !v)}>
                    + Add user
                  </button>
                </div>
              </div>

              {addOpen && (
                <div className="card" style={{ background: "var(--surface-soft)", marginBottom: 10 }}>
                  <div style={{ display: "grid", gap: 10, gridTemplateColumns: "1fr 1fr auto auto" }}>
                    <select
                      className="select"
                      value={addUserId}
                      onChange={(e) => setAddUserId(e.target.value)}
                    >
                      <option value="">Select user…</option>
                      {availablePeople.map((p) => (
                        <option key={p.account_id} value={p.account_id}>
                          {p.display_name ?? p.email}
                        </option>
                      ))}
                    </select>
                    <select
                      className="select"
                      value={addRoleId}
                      onChange={(e) => setAddRoleId(e.target.value)}
                    >
                      <option value="">Select role…</option>
                      {roleTypes
                        .filter((r) => !r.is_disabled)
                        .map((r) => (
                          <option key={r.id} value={r.id}>
                            {r.label} ({r.slug})
                          </option>
                        ))}
                    </select>
                    <button
                      className="btn btnPrimary"
                      disabled={busy || !addUserId || !addRoleId}
                      onClick={() => void addMapping()}
                    >
                      Add
                    </button>
                    <button className="btn" onClick={() => setAddOpen(false)} disabled={busy}>
                      Cancel
                    </button>
                  </div>
                  {addError && (
                    <div className="help" style={{ color: "var(--danger-text)", marginTop: 8 }}>
                      {addError}
                    </div>
                  )}
                  {roleTypes.length === 0 && (
                    <div className="help muted" style={{ marginTop: 8 }}>
                      No role types configured. Use <strong>Manage roles</strong> on the Customers
                      list to add one (e.g. AE, CSM).
                    </div>
                  )}
                </div>
              )}

              {mappings.length === 0 ? (
                <div className="muted" style={{ padding: 12 }}>
                  No users mapped yet. Add at least a primary user to enable fallback coverage.
                </div>
              ) : (
                <div className="tableWrap">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>User</th>
                        <th>Role</th>
                        <th style={{ textAlign: "center", width: 80 }}>Primary</th>
                        <th style={{ width: 100 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {mappings.map((m) => (
                        <tr key={m.id}>
                          <td>
                            {m.display_name ?? m.email}
                            <span className="muted" style={{ marginLeft: 8 }}>
                              {m.email}
                            </span>
                          </td>
                          <td>{m.role_label}</td>
                          <td style={{ textAlign: "center" }}>
                            <input
                              type="radio"
                              name="default-primary"
                              checked={m.is_primary}
                              onChange={() => !m.is_primary && void setPrimary(m.id)}
                            />
                          </td>
                          <td>
                            <button
                              className="adminListAction isDanger"
                              onClick={() => void removeMapping(m)}
                            >
                              Remove
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>

        <div className="modalScrollFooter">
          <div className="actions" style={{ justifyContent: "flex-end" }}>
            <button className="btn" onClick={onClose}>
              Cancel
            </button>
            <button className="btn btnPrimary" onClick={onClose}>
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
